// Parse Wagtail StreamField JSON to builder config with validation
import { getNextAvailableColor, CHART_COLORS } from './colorPalette';

const WAGTAIL_TYPE_MAP = {
    fenix_line_chart: 'line',
    fenix_stacked_area_chart: 'area',
    fenix_column_chart: 'column',
    fenix_bar_chart: 'bar',
    fenix_polar_chart: 'polar',
    fenix_tree_map_chart: 'treemap',
    fenix_timeline_chart: 'timeline',
    fenix_mixed_chart: 'mixed',
    fenix_heat_map_chart: 'heatmap',
};

const generateId = () => Math.random().toString(36).substr(2, 9);

const unwrapField = (fieldWrapper) => {
    if (!fieldWrapper) return '';
    if (typeof fieldWrapper === 'string') return fieldWrapper;
    if (fieldWrapper.value?.field) return fieldWrapper.value.field;
    if (fieldWrapper.field) return fieldWrapper.field;
    return '';
};

const unwrapDataset = (dataSource) => {
    if (!dataSource) return '';
    // Can be an array or a single object
    const source = Array.isArray(dataSource) ? dataSource[0] : dataSource;
    if (!source) return '';
    if (source.value?.dataset) return source.value.dataset;
    if (source.dataset) return source.dataset;
    if (typeof source === 'string') return source;
    return '';
};

// Parse a filter_query (or legacy series-level filter_params dict) into the
// editor's in-memory shape: a list of {field, op, value} objects with `op`
// always explicit (defaulting to 'eq' for legacy data without an operator).
const parseFilterParams = (filterQuery) => {
    if (!filterQuery) return [];

    const out = [];
    const source = filterQuery.filters ?? filterQuery.filter_params;
    if (!source) return out;

    if (Array.isArray(source)) {
        source.forEach((f) => {
            let field, op, value;
            // Wagtail ListBlock items serialize as {type: 'item', value: {...}}
            // in raw StreamField JSON; 'filter_item' covers older exports.
            if ((f.type === 'item' || f.type === 'filter_item') && f.value) {
                field = unwrapField(f.value.field);
                op = f.value.operator || 'eq';
                value = f.value.value?.value || f.value.value || '';
            } else {
                field = f.field || '';
                op = f.operator || f.op || 'eq';
                value = f.value || '';
            }
            if (field) {
                out.push({ field, op, value });
            }
        });
    } else if (typeof source === 'object') {
        // Legacy series-level filter_params dict — operator absent, defaults to eq
        Object.entries(source).forEach(([field, value]) => {
            out.push({ field, op: 'eq', value });
        });
    }

    return out;
};

const parseZone = (zone) => {
    if (!zone || !zone.axis) {
        return { axis: '', startValue: '', endValue: '' };
    }
    return {
        axis: zone.axis,
        startValue: zone.start_value || '',
        endValue: zone.end_value || '',
    };
};

const parseAnnotations = (annotations = []) => {
    if (!annotations || !Array.isArray(annotations)) return [];
    // Flatten Wagtail annotations to flat items for UI
    return annotations.flatMap((anno) => {
        const val = anno.value || anno;
        return (val.annotation_items || []).map((item) => ({
            xAxisCoord: item.x_axis_coord || '',
            xAxisRelativeAlignment: item.x_axis_relative_alignment || '',
            yAxisCoord: item.y_axis_coord || '',
            yAxisRelativeAlignment: item.y_axis_relative_alignment || '',
            text: item.text || '',
            showOnlyOnCountries: val.show_only_on_countries || [],
        }));
    });
};

const parseStandardSeries = (seriesArray, usedColors = []) => {
    if (!seriesArray || !Array.isArray(seriesArray)) return [];

    return seriesArray.map((s, index) => {
        const val = s.value || s;
        const serie = val.serie || {};

        const existingColor = serie.color;
        const color = existingColor || getNextAvailableColor([...usedColors]);
        if (!existingColor) usedColors.push(color);

        return {
            id: generateId(),
            dataSource: {
                type: 'dataset',
                value: unwrapDataset(val.data_source),
            },
            filterParams: parseFilterParams(val.filter_query || val),
            serie: {
                property: unwrapField(serie.property),
                label: serie.label || '',
                color: color,
                xAxisCategoryProperty:
                    unwrapField(serie.x_axis_category_property) ||
                    unwrapField(serie.xAxisCategoryProperty),
                negateSerieValues: serie.negate_serie_values || false,
                zone: parseZone(serie.zone),
            },
            annotations: parseAnnotations(val.annotations),
        };
    });
};

const parseMixedSeries = (seriesArray, usedColors = []) => {
    if (!seriesArray || !Array.isArray(seriesArray)) return [];

    return seriesArray.map((s, index) => {
        const val = s.value || s;
        const serie = val.serie || {};

        const existingColor = serie.color;
        const color = existingColor || getNextAvailableColor([...usedColors]);
        if (!existingColor) usedColors.push(color);

        return {
            id: generateId(),
            dataSource: {
                type: 'dataset',
                value: unwrapDataset(val.data_source),
            },
            filterParams: parseFilterParams(val.filter_query || val),
            seriesChartType: val.series_chart_type || 'line',
            serie: {
                property: unwrapField(serie.property),
                label: serie.label || '',
                color: color,
                xAxisCategoryProperty:
                    unwrapField(serie.x_axis_category_property) ||
                    unwrapField(serie.xAxisCategoryProperty),
                negateSerieValues: serie.negate_serie_values || false,
                zone: parseZone(serie.zone),
            },
            annotations: parseAnnotations(val.annotations),
        };
    });
};

const parseHeatmapSeries = (seriesArray, usedColors = []) => {
    if (!seriesArray || !Array.isArray(seriesArray)) return [];

    return seriesArray.map((s) => {
        const val = s.value || s;
        const serie = val.serie || {};

        const existingColor = serie.color;
        const color = existingColor || getNextAvailableColor([...usedColors]);
        if (!existingColor) usedColors.push(color);

        return {
            id: generateId(),
            dataSource: {
                type: 'dataset',
                value: unwrapDataset(val.data_source),
            },
            filterParams: parseFilterParams(val.filter_query || val),
            serie: {
                property: unwrapField(serie.property),
                label: serie.label || '',
                color: color,
                xAxisCategoryProperty:
                    unwrapField(serie.x_axis_category_property) ||
                    unwrapField(serie.xAxisCategoryProperty),
                yAxisCategoryProperty:
                    unwrapField(serie.y_axis_category_property) ||
                    unwrapField(serie.yAxisCategoryProperty),
                negateSerieValues: false,
                zone: { axis: '', startValue: '', endValue: '' },
            },
            annotations: [],
        };
    });
};

const parseTimelineSeries = (seriesArray) => {
    if (!seriesArray || !Array.isArray(seriesArray)) return [];

    return seriesArray.map((s) => {
        const val = s.value || s;
        const serie = val.serie || {};

        return {
            id: generateId(),
            dataSource: {
                type: 'dataset',
                value: unwrapDataset(val.data_source),
            },
            filterParams: parseFilterParams(val.filter_query || val),
            serie: {
                dateProperty: unwrapField(serie.date_property),
                titleProperty: unwrapField(serie.title_property),
                descriptionProperty: unwrapField(serie.description_property),
                // Compatibility fields
                property: unwrapField(serie.date_property),
                label: unwrapField(serie.title_property),
                color: '#4FA0FF',
                xAxisCategoryProperty: '',
                negateSerieValues: false,
                zone: { axis: '', startValue: '', endValue: '' },
            },
            annotations: [],
        };
    });
};

const parseTreemapSeries = (seriesArray, usedColors = []) => {
    if (!seriesArray || !Array.isArray(seriesArray))
        return { series: [], hierarchyLevels: [], colorMapping: [] };

    let hierarchyLevels = [];
    let colorMapping = [];

    const series = seriesArray.map((s) => {
        const val = s.value || s;
        const serie = val.serie || {};

        // Extract hierarchy_levels from series
        if (val.hierarchy_levels && val.hierarchy_levels.length > 0) {
            hierarchyLevels = val.hierarchy_levels.map((level) => ({
                property:
                    level.ontology_property || level.grouping_property || '',
                labelProperty: level.label_property || '',
                useForColoring:
                    level.use_for_coloring || level.useForColoring || false,
            }));
        }

        // Extract color_mapping from series
        if (val.color_mapping && val.color_mapping.length > 0) {
            colorMapping = val.color_mapping.map((cm) => ({
                ontologyProperty: cm.ontology_property || '',
                condition: cm.condition || 'equals',
                value: cm.value || '',
                caseSensitive: cm.case_sensitive || false,
                color: cm.color || '#4FA0FF',
            }));
        }

        const existingColor = serie.color;
        const color = existingColor || getNextAvailableColor([...usedColors]);
        if (!existingColor) usedColors.push(color);

        return {
            id: generateId(),
            dataSource: {
                type: 'dataset',
                value: unwrapDataset(val.data_source),
            },
            filterParams: parseFilterParams(val.filter_query || val),
            serie: {
                property: unwrapField(serie.property),
                label: serie.label || '',
                color: color,
                xAxisCategoryProperty: '',
                negateSerieValues: false,
                zone: { axis: '', startValue: '', endValue: '' },
            },
            annotations: [],
        };
    });

    return { series, hierarchyLevels, colorMapping };
};

const parseDynamicProperty = (dynamicProp) => {
    if (!dynamicProp) return null;

    const val = dynamicProp.value || dynamicProp;
    if (!val.property && !unwrapField(val.property)) return null;

    return {
        property: unwrapField(val.property),
        optionsSelectStyle: val.options_select_style || 'dropdown',
        populateAutomatically: val.populate_automatically || false,
        propertyFilterOptions: (val.property_filter_options || []).map(
            (opt) => ({
                value: opt.value || '',
                label: opt.label || opt.value || '',
            })
        ),
    };
};

export const wagtailToConfig = (wagtailJson) => {
    const errors = [];
    const warnings = [];

    // Validate basic structure
    if (!wagtailJson || typeof wagtailJson !== 'object') {
        return { success: false, errors: ['Invalid JSON: expected an object'] };
    }

    const { type, value } = wagtailJson;

    if (!type) {
        return { success: false, errors: ['Missing "type" field'] };
    }

    if (!value || typeof value !== 'object') {
        return { success: false, errors: ['Missing or invalid "value" field'] };
    }

    // Check if chart type is supported
    const chartType = WAGTAIL_TYPE_MAP[type];
    if (!chartType) {
        return {
            success: false,
            errors: [`Unsupported chart type: "${type}"`],
        };
    }

    // Check for color_mapping on treemap
    if (chartType === 'treemap') {
        const series = value.series || [];
        series.forEach((s, i) => {
            const val = s.value || s;
            if (val.color_mapping && val.color_mapping.length > 0) {
                warnings.push(
                    `Series ${i + 1} has color_mapping. This will be preserved but cannot be edited in the builder.`
                );
            }
        });
    }

    // Collect warnings for unknown fields
    const knownFields = new Set([
        'title',
        'display_large_title',
        'show_title_in_toc',
        'hide_chart_for_countries',
        'grid_span',
        'include_in_overview',
        'aggregate_data_by_category',
        'y_axis_label',
        'y_axis_min',
        'y_axis_max',
        'y_axis_tick_interval',
        'decimal_places',
        'y_axis_stack_values',
        'y_axis_category_ordering',
        'y_axis_category_sorting_type',
        'y_axis_label_formattting',
        'x_axis_label',
        'x_axis_category_ordering',
        'x_axis_category_sorting_type',
        'x_axis_label_formattting',
        'enable_line_symbols',
        'stacking_type',
        'polar_chart_value_type',
        'layout_algorithm',
        'border_width',
        'border_color',
        'space_points_evenly',
        'date_ordering',
        'date_formattting',
        'color',
        'color_scheme',
        'series',
        'dynamic_property',
    ]);

    Object.keys(value).forEach((key) => {
        if (!knownFields.has(key)) {
            warnings.push(`Unknown field "${key}" will be ignored`);
        }
    });

    // If there are blocking errors, return early
    if (errors.length > 0) {
        return { success: false, errors, warnings };
    }

    const isTimeline = chartType === 'timeline';
    const isTreemap = chartType === 'treemap';
    const isHeatmap = chartType === 'heatmap';

    // Build the config
    const config = {
        chartType,
        title: value.title || '',
        displayLargeTitle: value.display_large_title || false,
        showTitleInToc: value.show_title_in_toc || false,
        gridSpan: value.grid_span || '1',
        includeInOverview: value.include_in_overview || false,
        hideChartForCountries: value.hide_chart_for_countries || [],
        previewMode: 'full',
    };

    if (isHeatmap) {
        config.decimalPlaces = value.decimal_places ?? null;
        config.colorScheme = value.color_scheme || 'blue';
        config.yAxis = {
            label: value.y_axis_label || '',
            min: null,
            max: null,
            tickInterval: null,
            stackValues: false,
            categoryOrdering: value.y_axis_category_ordering || 'asc',
            categorySortingType: value.y_axis_category_sorting_type || 'string',
            labelFormatting: value.y_axis_label_formattting || 'none',
        };
        config.xAxis = {
            label: value.x_axis_label || '',
            categoryOrdering: value.x_axis_category_ordering || 'asc',
            categorySortingType: value.x_axis_category_sorting_type || 'string',
            labelFormatting: value.x_axis_label_formattting || 'none',
        };

        config.series = parseHeatmapSeries(value.series || []);
    } else if (isTimeline) {
        config.spacePointsEvenly = value.space_points_evenly || false;
        config.dateOrdering = value.date_ordering || 'asc';
        config.dateFormatting = value.date_formattting || 'none'; // Note: Wagtail typo
        config.timelineColor = value.color || 'auto';
        config.series = parseTimelineSeries(value.series || []);

        // Timeline-specific default axis config
        config.yAxis = {
            label: '',
            min: null,
            max: null,
            tickInterval: null,
            stackValues: false,
        };
        config.xAxis = {
            label: '',
            categoryOrdering: 'asc',
            categorySortingType: 'number',
            labelFormatting: 'none',
        };
    } else if (isTreemap) {
        config.layoutAlgorithm = value.layout_algorithm || 'sliceAndDice';
        config.borderWidth = value.border_width ?? 3;
        config.borderColor = value.border_color || '#fff';

        const { series, hierarchyLevels, colorMapping } = parseTreemapSeries(
            value.series || []
        );
        config.series = series;
        config.hierarchyLevels = hierarchyLevels;
        config.colorMapping = colorMapping;

        config.dynamicProperty = parseDynamicProperty(value.dynamic_property);

        // Treemap default axis config
        config.yAxis = {
            label: '',
            min: null,
            max: null,
            tickInterval: null,
            stackValues: false,
        };
        config.xAxis = {
            label: '',
            categoryOrdering: 'asc',
            categorySortingType: 'number',
            labelFormatting: 'none',
        };
    } else {
        // Standard charts
        config.decimalPlaces = value.decimal_places ?? null;
        config.aggregateDataByCategory =
            value.aggregate_data_by_category || false;
        config.yAxis = {
            label: value.y_axis_label || '',
            min: value.y_axis_min ?? null,
            max: value.y_axis_max ?? null,
            tickInterval: value.y_axis_tick_interval ?? null,
            stackValues: value.y_axis_stack_values || false,
        };
        config.xAxis = {
            label: value.x_axis_label || '',
            categoryOrdering: value.x_axis_category_ordering || 'asc',
            categorySortingType: value.x_axis_category_sorting_type || 'number',
            labelFormatting: value.x_axis_label_formattting || 'none', // Note: Wagtail typo
        };

        config.series =
            chartType === 'mixed'
                ? parseMixedSeries(value.series || [])
                : parseStandardSeries(value.series || []);

        // Chart-specific fields
        if (
            chartType === 'line' ||
            chartType === 'area' ||
            chartType === 'polar' ||
            chartType === 'mixed'
        ) {
            config.enableLineSymbols = value.enable_line_symbols || false;
        }

        if (chartType === 'area') {
            config.stackingType = value.stacking_type || 'normal';
        }

        if (chartType === 'polar') {
            config.polarChartValueType = value.polar_chart_value_type || 'line';
        }

        config.dynamicProperty = parseDynamicProperty(value.dynamic_property);
    }

    // Ensure at least one series exists
    if (!config.series || config.series.length === 0) {
        config.series = [
            {
                id: generateId(),
                dataSource: { type: 'dataset', value: '' },
                filterParams: [],
                serie: {
                    property: '',
                    label: '',
                    color: CHART_COLORS[0],
                    xAxisCategoryProperty: '',
                    yAxisCategoryProperty: '',
                    negateSerieValues: false,
                    zone: { axis: '', startValue: '', endValue: '' },
                },
                annotations: [],
            },
        ];
    }

    return {
        success: true,
        config,
        warnings: warnings.length > 0 ? warnings : undefined,
    };
};

export default wagtailToConfig;
